import { logSwallowed } from '@/lib/errors';
// ============================================================================
// FileListTab.tsx — вкладка «Файлы» (системный справочник «ФайлыПоДокументам»):
//   - drag&drop и массовая загрузка файлов с индикацией прогресса;
//   - массовое скачивание выбранных; клик по строке — форма файла;
//   - записи СКРЫВАЮТСЯ (is_marked) вместо удаления; окончательное удаление —
//     только администратор (global:admin).
// SSoT: данные — сервер (dataService); содержимое — FileService.
// ============================================================================

import { confirmDialog, alertDialog } from '@/lib/dialogs';
import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { dataService } from '@/lib/dataService';
import { getSDK } from '@/store/sdkStore';
import { ensureFileBlob, downloadFileWithProgress, getFileBlob, onDownloadsChange, getDownloadJobs, pauseDownload, abortDownload } from '@/lib/fileSync';
import { uploadFile, onUploadsChange, getUploadJobs, abortUpload, pauseUpload, resumeUpload } from '@/lib/chunkedUpload';
import { useAuthStore } from '@/store/authStore';
import { createLogger } from '@/lib/logger';
import { FileCollection, type FileCollectionView, FILE_VIEW_LABELS, type FileDetail } from '@/components/collection/FileCollection';
import { CameraCaptureDialog } from '@/components/media/CameraCaptureDialog';
import { captureLocation, type GeoPoint } from '@/lib/geolocation';
import { CopyButton } from '@/components/ui/CopyButton';

const log = createLogger('FileListTab');

interface FileListTabProps {
  objectName: string;
  ownerId: string;
  /** Экшены ВЛАДЕЛЬЦА (как 1С: команды объекта доступны из связанных файлов):
   *  например «Загрузить выписку»/«Загрузить из файлов» документа. Формирует
   *  владелец (AutoObjectForm) из actionBar кастомной формы — SRP: FileListTab
   *  только рендерит. */
  actions?: Array<{ label: string; onClick: () => void }>;
}

export function FileListTab({ objectName, ownerId, actions }: FileListTabProps) {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Камера (универсальный компонент) и привязка координат (чувствительные
  // данные: собираются только по явному выбору пользователя)
  const [cameraOpen, setCameraOpen] = useState(false);
  const isAdmin = useAuthStore((s) => s.isAdmin());
  // ЕДИНЫЙ виджет коллекции: вид (persist localStorage — настройка пользователя);
  // ПО УМОЛЧАНИЮ — КАРТОЧКИ (как списки объектов в 1С 8.5)
  const [view, setView] = useState<FileCollectionView>(() => {
    try { return (localStorage.getItem('hydra_files_view') as FileCollectionView) || 'card'; } catch { return 'card'; }
  });
  const changeView = (v: FileCollectionView) => { setView(v); try { localStorage.setItem('hydra_files_view', v); } catch { /* ignore */ } };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Новая (несохранённая) запись: связей нет — НЕ запрашиваем (ListFiles с
    // пустым ownerId вернул бы ВСЕ файлы базы). Вкладка пустая, загрузка выключена.
    if (!ownerId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      // SSoT — сервер: ListFiles(ownerId, includeLinked) через РЕГИСТР file_links:
      // к файлам объекта добавляются файлы СВЯЗАННЫХ ЗАДАЧ (subject_id = ownerId) —
      // они остаются в задачах, но доступны и здесь (включая заявки).
      const r: any = await getSDK().files.listFiles({ ownerId, includeLinked: true } as any);
      setRows((r?.items || []).map((f: any) => ({
        id: f.id,
        file_name: f.fileName,
        file_size: f.fileSize,
        file_type: f.fileType,
        // Владелец: пусто/текущий — свой файл; иной — файл связанной задачи.
        owner_id: f.ownerId || '',
        created_at: f.uploadedAt,
        uploaded_by: f.uploadedBy,
      })));
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Глобальный перехват drag&drop: иначе браузер/WebView2 открывает перетаскиваемый
  // файл (например, в блокноте) вместо обработки нашей зоной.
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  const uploadFiles = async (files: File[], opts?: { location?: GeoPoint | null }) => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    // Координаты — «ВСЕГДА при возможности» (SSoT — captureLocation, единый механизм):
    // при загрузке файлов пытаемся снять точку; отказ/недоступность НЕ блокируют
    // загрузку, но ПОКАЗЫВАЮТСЯ (нет тихих фоллбэков). Предупреждение показываем
    // ПОСЛЕ загрузки: load() сбрасывает error — иначе сообщение «мелькало» и
    // пользователь думал, что загрузка не удалась.
    let loc: GeoPoint | null | undefined = opts?.location;
    let geoWarning: string | null = null;
    if (loc === undefined) {
      try {
        loc = await captureLocation();
      } catch (e: any) {
        geoWarning = e?.message || 'не удалось определить местоположение';
      }
    }
    let done = 0;
    const total = files.length;
    setProgress({ done: 0, total });
    let failed = false;
    try {
      // ЕДИНЫЙ механизм загрузки (offline-first, чанки с прогрессом/отменой).
      // onComplete — после сборки файла на сервере (fileId) → load().
      // Передаём geoLat/geoLon (снятые при возможности) в UploadOptions — иначе
      // координаты терялись (баг: loc вычислялась, но не доезжала до сервера).
      const jobs = files.map((file) => uploadFile(file, {
        ownerId,
        geoLat: loc?.lat ?? 0,
        geoLon: loc?.lon ?? 0,
        onComplete: async () => {
          done++;
          setProgress({ done, total });
          await load();
        },
      }));
      // Ждём завершения всех (статусы — в менеджере передач)
      await Promise.all(jobs);
      await load();
    } catch (e: any) {
      failed = true;
      setError(e?.message || 'Ошибка загрузки файлов');
    } finally {
      setProgress(null);
      setBusy(false);
      // Загрузка успешна, но без координат — устойчивое предупреждение (не мелькает).
      if (geoWarning && !failed) setError('Файлы загружены без координат: ' + geoWarning);
    }
  };

  const downloadSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of selected) {
        // Оффлайн-first: содержимое из кэша (IndexedDB) или с сервера.
        // Для больших файлов — ЧАНКОВОЕ скачивание (прогресс/пауза/отмена
        // в панели ActiveDownloads; Range-докачка; SSoT — fileSync).
        let blob: Blob;
        let fileName: string;
        try {
          const cached = await getFileBlob(id);
          if (cached) {
            blob = cached; fileName = 'file';
          } else {
            const r = await downloadFileWithProgress(id);
            blob = r.blob; fileName = r.fileName;
          }
        } catch (e: any) {
          // Пауза — не ошибка (продолжение — повторным вызовом)
          if (e?.paused) continue;
          throw e;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'file';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка скачивания');
    } finally {
      setBusy(false);
    }
  };

  // Скрытие ОДНОГО файла (действие в коллекции) — как в 1С: скрытие вместо удаления
  const hideOne = async (id: string) => {
    // Файл СВЯЗАННОЙ ЗАДАЧИ изменяется в самой задаче — доступ через объект
    // только для чтения (скачивание/предпросмотр).
    const row = rows.find((x) => x.id === id);
    if (row && row.owner_id && String(row.owner_id) !== ownerId) return;
    try {
      await dataService.update('public', objectName, id, { is_marked: true });
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка скрытия');
    }
  };

  const hideSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of selected) {
        const row = rows.find((x) => x.id === id);
        if (row && row.owner_id && String(row.owner_id) !== ownerId) continue; // файлы задач — в задаче
        await dataService.update('public', objectName, id, { is_marked: true });
      }
      setSelected(new Set());
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка скрытия');
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0 || !isAdmin) return;
    if (!await confirmDialog(`Окончательно удалить ${selected.size} файл(ов)?`)) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of selected) {
        const row = rows.find((x) => x.id === id);
        if (row && row.owner_id && String(row.owner_id) !== ownerId) continue; // файлы задач — в задаче
        await getSDK().files.deleteFile({ id });
      }
      setSelected(new Set());
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка удаления');
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatSize = (v: unknown) => {
    const n = Number(v);
    if (!n) return '';
    if (n >= 1048576) return (n / 1048576).toFixed(1) + ' МБ';
    if (n >= 1024) return (n / 1024).toFixed(1) + ' КБ';
    return n + ' Б';
  };

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 space-y-2 ${dragOver ? 'ring-2 ring-primary rounded-md' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(Array.from(e.dataTransfer.files || [])); }}
    >
      {/* Панель команд */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Экшены ВЛАДЕЛЬЦА (команды объекта — из связанных файлов, как 1С) */}
        {actions && actions.length > 0 && (
          <>
            {actions.map((a, i) => (
              <Button key={i} size="sm" variant="outline" className="h-6 text-[11px]" onClick={a.onClick} disabled={busy}>
                {a.label}
              </Button>
            ))}
            <span className="w-px h-4 bg-border mx-0.5" />
          </>
        )}
        <label className={`text-xs border rounded px-2 py-1 cursor-pointer hover:bg-muted/30 ${dragOver ? 'border-primary bg-primary/10' : ''} ${ownerId ? '' : 'opacity-40 pointer-events-none'}`} title={ownerId ? '' : 'Сохраните запись, чтобы прикреплять файлы'}>
          {busy && progress ? `Загрузка ${progress.done}/${progress.total}…` : 'Загрузить файлы'}
          <input
            type="file"
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) uploadFiles(fs); }}
          />
        </label>
        <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setCameraOpen(true)} disabled={busy || !ownerId} title={ownerId ? '' : 'Сохраните запись, чтобы прикреплять файлы'}>
          Камера
        </Button>
        <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={downloadSelected} disabled={busy || selected.size === 0}>
          Скачать выбранные ({selected.size})
        </Button>
        <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={hideSelected} disabled={busy || selected.size === 0} title="Пометить на удаление: файл скрывается, «Удалить окончательно» — только админ">
          Пометить на удаление ({selected.size})
        </Button>
        {isAdmin && (
          <Button size="sm" variant="outline" className="h-6 text-[11px] text-destructive" onClick={deleteSelected} disabled={busy || selected.size === 0}>
            Удалить окончательно
          </Button>
        )}
        {/* ЕДИНЫЙ виджет коллекции: переключатель вида (Карточка/Галерея/Тайл/Список/Таблица) */}
        <select
          className="h-6 text-[10px] border rounded bg-background px-1 ml-auto"
          value={view}
          onChange={(e) => changeView(e.target.value as FileCollectionView)}
          title="Вид коллекции файлов"
        >
          {(Object.keys(FILE_VIEW_LABELS) as FileCollectionView[]).map((v) => (
            <option key={v} value={v}>{FILE_VIEW_LABELS[v]}</option>
          ))}
        </select>
      </div>

      {/* АКТИВНЫЕ ПЕРЕДАЧИ (единый менеджер chunkedUpload): прогресс,
          пауза/возобновление, отмена; офлайн — «ожидает сети» */}
      <ActiveUploads />
      {/* АКТИВНЫЕ СКАЧИВАНИЯ (fileSync, Range-докачка): прогресс/пауза/отмена */}
      <ActiveDownloads />

      {error && (
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1">
          <span className="flex-1 min-w-0 break-words">{error}</span>
          <CopyButton text={error} compact />
        </div>
      )}
      {progress && !busy && <div className="text-xs text-muted-foreground">Загружено {progress.done} из {progress.total}</div>}

      {/* Зона списка — ЕДИНЫЙ виджет коллекции (вид переключается выше) */}
      <div className={`flex-1 min-h-0 border rounded-md overflow-auto p-2 ${dragOver ? 'border-primary bg-primary/5' : ''}`}>
        {loading ? (
          <div className="text-xs text-muted-foreground p-3">Загрузка файлов…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground p-3 italic">
            {ownerId
              ? 'Нет файлов. Перетащите файлы сюда или нажмите «Загрузить файлы».'
              : 'Файлы появятся после сохранения записи.'}
          </div>
        ) : (
          <FileCollection
            files={rows.map((r) => ({
              id: String(r.id),
              fileName: String(r.file_name ?? r.name ?? ''),
              fileSize: Number(r.file_size || 0),
              fileType: String(r.file_type ?? ''),
              uploadedAt: String(r.created_at ?? ''),
            }))}
            view={view}
            onPick={(f) => toggleRow(f.id)}
            onRemove={(f) => { void hideOne(f.id); }}
            actions={view !== 'table'}
            selectedIds={selected}
            emptyText="Нет файлов"
          />
        )}
      </div>

      {editingId && (
        <AutoObjectForm
          objectType="catalog"
          objectName={objectName}
          objectId={editingId}
          onClose={() => { setEditingId(null); load(); }}
        />
      )}

      {/* Универсальный захват с камеры: снимок → загрузка. Координаты снимает
          сам uploadFiles («всегда при возможности») — единая точка, без дублей. */}
      <CameraCaptureDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCaptured={({ file }) => { setCameraOpen(false); void uploadFiles([file]); }}
      />
    </div>
  );
}

// ============================================================================
// ActiveDownloads — активные скачивания (fileSync, Range-докачка):
// прогресс, пауза/возобновление, отмена. Несколько одновременно.
// ============================================================================
function ActiveDownloads() {
  const [, force] = useState(0);
  useEffect(() => onDownloadsChange(() => force((x) => x + 1)), []);
  const jobs = getDownloadJobs();
  const active = jobs.filter((j) => j.status === 'downloading' || j.status === 'paused');
  if (active.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {active.map((j) => (
        <div key={j.id} className="flex items-center gap-2 text-[11px] border rounded px-2 py-1.5 bg-muted/10">
          <span className="truncate flex-1 min-w-0">{j.fileName || j.id.slice(0, 8)} · {j.status === 'paused' ? 'пауза' : 'скачивание'}</span>
          <span className="shrink-0 text-muted-foreground">{Math.round(j.progress * 100)}%</span>
          <div className="h-1.5 w-16 shrink-0 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(j.progress * 100)}%` }} />
          </div>
          {j.status === 'downloading' ? (
            <button className="shrink-0 text-[10px] underline text-muted-foreground hover:text-foreground" onClick={() => pauseDownload(j.id)}>Пауза</button>
          ) : (
            <button className="shrink-0 text-[10px] underline text-primary" onClick={() => void downloadFileWithProgress(j.id).catch((e) => { logSwallowed(e, 'components/autoforms/FileListTab.tsx:407'); return undefined; })}>Продолжить</button>
          )}
          <button className="shrink-0 text-[10px] underline text-destructive hover:text-destructive/70" onClick={() => abortDownload(j.id)}>Отмена</button>
        </div>
      ))}
    </div>
  );
}
// ============================================================================
// ActiveUploads — активные передачи файлов (ЕДИНЫЙ менеджер chunkedUpload):
// прогресс, пауза/возобновление, отмена; офлайн — «ожидает сети».
// ============================================================================
function ActiveUploads() {
  const [, force] = useState(0);
  useEffect(() => onUploadsChange(() => force((x) => x + 1)), []);
  const jobs = getUploadJobs();
  const active = jobs.filter((j) => j.status === 'queued' || j.status === 'uploading' || j.status === 'pending' || j.status === 'paused');
  if (active.length === 0) return null;
  const statusLabel = (s: string) =>
    s === 'queued' ? 'ожидает сети' : s === 'paused' ? 'пауза' : s === 'uploading' ? 'загрузка' : 'ожидание';
  return (
    <div className="space-y-1.5">
      {active.map((j) => (
        <div key={j.id} className="flex items-center gap-2 text-[11px] border rounded px-2 py-1.5 bg-muted/10">
          <span className="truncate flex-1 min-w-0">{j.fileName} · {statusLabel(j.status)}</span>
          <span className="shrink-0 text-muted-foreground">{j.status === 'queued' ? '' : `${Math.round(j.progress * 100)}%`}</span>
          {j.status !== 'queued' && (
            <div className="h-1.5 w-16 shrink-0 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(j.progress * 100)}%` }} />
            </div>
          )}
          {j.status === 'uploading' ? (
            <button className="shrink-0 text-[10px] underline text-muted-foreground hover:text-foreground" onClick={() => pauseUpload(j.id)}>Пауза</button>
          ) : j.status === 'paused' ? (
            <button className="shrink-0 text-[10px] underline text-primary" onClick={() => void resumeUpload(j.id)}>Продолжить</button>
          ) : null}
          <button className="shrink-0 text-[10px] underline text-destructive hover:text-destructive/70" onClick={() => void abortUpload(j.id)}>Отмена</button>
        </div>
      ))}
    </div>
  );
}
